import { useEffect, useRef, useState } from 'react';
import type { MotionValue } from 'framer-motion';
import { AnimatePresence, motion, useMotionValueEvent, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { BrandIcons } from '@/components/common/BrandIcons';
import { ArrowRight, Check, X } from 'phosphor-react';

// Integration logos using BrandIcons
// Removed top/left hardcoded positioning in favor of grid/flex or controlled absolute positioning relative to container center
// But keeping absolute for the "floating" effect, just adjusting how they move

interface FloatingCardProps {
  name: string;
  icon: React.ElementType;
  color: string;
  initialPos: { top?: string; bottom?: string; left?: string; right?: string };
  delay: number;
  direction: 'left' | 'right';
  parallaxSpeed: number;
  scrollProgress: MotionValue<number>;
  seed: number;
}

function FloatingCard({ name, icon: Icon, color, initialPos, delay, direction, parallaxSpeed, scrollProgress, seed }: FloatingCardProps) {
  const baseRotate = direction === 'left' ? -3.5 : 3.5;
  const visibility = useTransform(scrollProgress, [0.12, 0.18, 0.8, 0.92], [0, 1, 1, 0]);
  const x = useTransform(scrollProgress, (v) => {
    const t = Math.max(0, Math.min(1, (v - 0.12) / 0.76));
    return Math.sin((t + seed) * Math.PI * 2) * (18 + Math.abs(parallaxSpeed) * 18);
  });
  const y = useTransform(scrollProgress, (v) => {
    const t = Math.max(0, Math.min(1, (v - 0.12) / 0.76));
    return (t - 0.5) * parallaxSpeed * 70 + Math.cos((t + seed * 0.8) * Math.PI * 2) * 10;
  });
  const rotate = useTransform(scrollProgress, (v) => {
    const t = Math.max(0, Math.min(1, (v - 0.12) / 0.76));
    const wobble = Math.sin((t + seed) * Math.PI * 2) * 1.1;
    return baseRotate + wobble;
  });
  const scale = useTransform(scrollProgress, (v) => {
    const t = Math.max(0, Math.min(1, (v - 0.12) / 0.76));
    const center = Math.max(0, 1 - Math.abs(t - 0.5) * 2);
    const breathe = Math.sin((t + seed * 0.7) * Math.PI * 2) * 0.012;
    return 0.965 + center * 0.065 + breathe;
  });
  const exitY = useTransform(scrollProgress, [0.8, 0.92], [0, -58]);

  return (
    <div style={{ ...initialPos }} className="absolute hidden md:block z-20 will-change-transform">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
        style={{ x, y, rotate, scale, opacity: visibility, translateY: exitY }}
        className="relative rounded-2xl p-4 backdrop-blur-md transition-transform duration-300 hover:scale-[1.04]"
      >
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, color-mix(in oklab, var(--card-bg) 92%, var(--foreground) 8%) 0%, color-mix(in oklab, var(--card-bg) 96%, transparent) 100%)',
            border: '1px solid color-mix(in oklab, var(--card-border) 70%, transparent)',
            boxShadow: '0 0 0 1px color-mix(in oklab, var(--card-border) 50%, transparent) inset',
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_88%,transparent)] relative"
            style={{ color: name === 'Notion' || name === 'GitHub' ? 'var(--foreground)' : color }}
          >
            <Icon width={24} height={24} />
          </div>
          <div className="pr-2">
            <p className="theme-text font-medium text-sm">{name}</p>
            <div className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_88%,transparent)]">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" />
              <p className="theme-text-muted text-xs">Conectado</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function IntegrationsSection() {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);

  const titleText = t('integrations.title');
  const subtitleText = t('integrations.subtitle');

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const titleProgress = useTransform(scrollYProgress, [0.18, 0.46], [0, 1], { clamp: true });
  const subtitleProgress = useTransform(scrollYProgress, [0.32, 0.62], [0, 1], { clamp: true });

  const [isPinned, setIsPinned] = useState(false);
  const [titleCount, setTitleCount] = useState(0);
  const [subtitleCount, setSubtitleCount] = useState(0);

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    setIsPinned(v > 0.12 && v < 0.95);
  });

  useMotionValueEvent(titleProgress, 'change', (v) => {
    const next = Math.max(0, Math.min(titleText.length, Math.round(v * titleText.length)));
    setTitleCount(next);
  });

  useMotionValueEvent(subtitleProgress, 'change', (v) => {
    const next = Math.max(0, Math.min(subtitleText.length, Math.round(v * subtitleText.length)));
    setSubtitleCount(next);
  });

  const showComparisonCard = false;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(isPinned ? 'light' : 'dark');
    return () => {
      root.classList.remove('light', 'dark');
      root.classList.add('dark');
    };
  }, [isPinned]);

  return (
    <section
      ref={sectionRef}
      id="integrations"
      data-scroll-section
      className="relative px-6 theme-bg min-h-[320vh] py-32 overflow-hidden"
    >
      <AnimatePresence>
        {isPinned && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 z-30 pointer-events-none flex items-center justify-center"
          >
            <div className="mx-auto max-w-7xl w-full px-6 text-center">
              <h2
                className="relative z-10 text-3xl md:text-5xl lg:text-6xl font-light tracking-tight mb-6 pb-2 bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(to right bottom, var(--foreground), color-mix(in oklab, var(--foreground) 70%, transparent))`,
                }}
              >
                {titleText.slice(0, titleCount)}
              </h2>
              <p className="text-lg max-w-2xl mx-auto text-[color:var(--foreground-muted)]">
                {subtitleText.slice(0, subtitleCount)}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isPinned && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 z-20 pointer-events-none"
          >
            <FloatingCard
              name="ClickUp" icon={BrandIcons.ClickUp} color="#7B68EE"
              initialPos={{ top: '64px', left: '6%' }}
              delay={0.15} direction="left" parallaxSpeed={-1.25} scrollProgress={scrollYProgress} seed={0.12}
            />
            <FloatingCard
              name="Notion" icon={BrandIcons.Notion} color="#FFFFFF"
              initialPos={{ top: '92px', right: '10%' }}
              delay={0.2} direction="right" parallaxSpeed={0.95} scrollProgress={scrollYProgress} seed={0.38}
            />
            <FloatingCard
              name="Jira" icon={BrandIcons.Jira} color="#0052CC"
              initialPos={{ top: '340px', left: '2%' }}
              delay={0.25} direction="left" parallaxSpeed={-0.9} scrollProgress={scrollYProgress} seed={0.54}
            />
            <FloatingCard
              name="Slack" icon={BrandIcons.Slack} color="#E01E5A"
              initialPos={{ top: '360px', right: '3%' }}
              delay={0.3} direction="right" parallaxSpeed={-0.75} scrollProgress={scrollYProgress} seed={0.72}
            />
            <FloatingCard
              name="GitHub" icon={BrandIcons.GitHub} color="#FFFFFF"
              initialPos={{ top: '160px', left: '18%' }}
              delay={0.35} direction="left" parallaxSpeed={0.62} scrollProgress={scrollYProgress} seed={0.21}
            />
            <FloatingCard
              name="Google Drive" icon={BrandIcons.GoogleDrive} color="#4285F4"
              initialPos={{ top: '190px', right: '20%' }}
              delay={0.4} direction="right" parallaxSpeed={-0.68} scrollProgress={scrollYProgress} seed={0.44}
            />
            <FloatingCard
              name="Dropbox" icon={BrandIcons.Dropbox} color="#0061FF"
              initialPos={{ bottom: '90px', left: '10%' }}
              delay={0.45} direction="left" parallaxSpeed={0.8} scrollProgress={scrollYProgress} seed={0.63}
            />
            <FloatingCard
              name="Zoom" icon={BrandIcons.Zoom} color="#2D8CFF"
              initialPos={{ bottom: '70px', right: '14%' }}
              delay={0.5} direction="right" parallaxSpeed={-0.52} scrollProgress={scrollYProgress} seed={0.83}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background gradient */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: `radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 90%)',
          WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 90%)'
        }}
      />

      <div className="mx-auto max-w-7xl relative h-[320vh]">
        {showComparisonCard && (
        <motion.div
          style={{}}
          className="relative flex justify-center z-0"
        >
          {/* Main comparison card */}
          <div
            className="relative flex flex-col md:flex-row rounded-3xl overflow-hidden w-full max-w-5xl border border-white/5"
            style={{
              background: 'linear-gradient(180deg, rgba(24, 24, 27, 0.8) 0%, rgba(9, 9, 11, 0.95) 100%)',
              boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.05) inset, 0 20px 60px -20px rgba(0, 0, 0, 0.5)',
            }}
          >
            {/* Left side - Other tools */}
            <div
              className="relative flex w-full md:w-1/2 flex-col items-center justify-between gap-10 py-16 px-8 bg-zinc-900/30"
            >
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <h3 className="text-2xl font-light text-zinc-200 tracking-tight">Ferramentas fragmentadas</h3>
                <p className="text-zinc-500 text-sm flex items-center gap-2 bg-red-500/5 px-3 py-1 rounded-full border border-red-500/10">
                  <X className="w-3.5 h-3.5 text-red-400" weight="bold" />
                  <span className="text-red-300">Sem contexto unificado</span>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 opacity-60 grayscale-[0.5] hover:grayscale-0 transition-all duration-500">
                {[BrandIcons.ClickUp, BrandIcons.Notion, BrandIcons.Slack, BrandIcons.GitHub].map((Icon, i) => (
                  <div
                    key={i}
                    className="w-16 h-16 rounded-2xl bg-zinc-800/50 flex items-center justify-center text-zinc-400 border border-white/5"
                  >
                    <Icon width={28} height={28} />
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center z-20 shadow-xl hidden md:flex">
              <ArrowRight className="w-4 h-4 text-zinc-400" weight="bold" />
            </div>

            {/* Right side - Paladino */}
            <div
              className="relative flex w-full md:w-1/2 flex-col items-center justify-between gap-10 py-16 px-8"
              style={{
                background: 'linear-gradient(145deg, rgba(20, 20, 23, 0.4) 0%, rgba(139, 92, 246, 0.05) 100%)',
              }}
            >
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <h3 className="text-2xl font-light text-white tracking-tight">Paladino Studio</h3>
                <p className="text-zinc-500 text-sm flex items-center gap-2 bg-emerald-500/5 px-3 py-1 rounded-full border border-emerald-500/10">
                  <Check className="w-3.5 h-3.5 text-emerald-400" weight="bold" />
                  <span className="text-emerald-300">Apps + dados + ações</span>
                </p>
              </div>

              <div className="relative w-full max-w-[280px]">
                <div
                  className="rounded-2xl p-4 border border-purple-500/20 bg-zinc-900/80 backdrop-blur-sm relative z-10 shadow-2xl shadow-purple-900/20"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center">
                      <svg viewBox="0 0 40 48" fill="white" className="w-5 h-5">
                        <path clipRule="evenodd" d="m20 41.1364c1.8693 0 3.6688-.2994 5.353-.8527-1.7175-.2891-3.5271-.8718-5.3517-1.7289-1.8251.8573-3.6351 1.4402-5.3529 1.7294 1.6838.553 3.4828.8522 5.3516.8522zm-16.28276-11.7805c.28897-1.7183.87187-3.529 1.72942-5.3547-.8578-1.8258-1.44092-3.6366-1.73007-5.3551-.55352 1.6844-.85295 3.4842-.85295 5.3539 0 1.8704.29966 3.6709.8536 5.3559zm3.60425-2.0098c-.16001.4683-.29664.9297-.40971 1.382-.88774 3.5509-.23652 5.9879 1.06875 7.2932 1.30527 1.3052 3.74227 1.9565 7.29317 1.0687.4525-.1131.914-.2498 1.3824-.4098-1.7678-1.1549-3.5052-2.5591-5.1409-4.1948-1.63517-1.6352-3.03901-3.372-4.19371-5.1393zm12.67971 7.8448c-2.1428-1.1725-4.3241-2.7862-6.3647-4.8268-2.0401-2.0401-3.6535-4.2207-4.82586-6.363 1.17244-2.1428 2.78616-4.324 4.82676-6.3646 2.0404-2.0404 4.2213-3.6539 6.3638-4.8263 2.1424 1.1723 4.3231 2.7858 6.3632 4.826 2.0407 2.0407 3.6545 4.222 4.827 6.3649-1.1724 2.1424-2.7859 4.3231-4.8261 6.3633-2.0404 2.0405-4.2215 3.6541-6.3641 4.8265zm12.6793-7.8447c-1.1548 1.7674-2.5586 3.5043-4.1939 5.1395-1.6354 1.6355-3.3726 3.0395-5.1402 4.1944.4682.1599.9295.2965 1.3817.4096 3.5509.8877 5.9879.2365 7.2932-1.0688 1.3052-1.3052 1.9565-3.7422 1.0687-7.2932-.113-.4521-.2496-.9133-.4095-1.3815zm1.8748-3.345c.8572-1.8245 1.44-3.634 1.7293-5.3514.5528 1.6834.8518 3.4819.8518 5.3502 0 1.869-.2993 3.6682-.8524 5.3521-.2891-1.7172-.8718-3.5265-1.7287-5.3509zm-9.2009-16.28444c-1.7179.28914-3.528.87204-5.3531 1.72941-1.8256-.85764-3.6361-1.44069-5.3544-1.72984 1.6842-.55336 3.4837-.85269 5.3531-.85269 1.8699 0 3.6698.29949 5.3544.85312zm8.7037 2.05754c-3.6125-3.57015-8.5779-5.7743-14.0581-5.7743-11.0457 0-20 8.9543-20 20s8.9543 20 20 20c5.488 0 10.4598-2.2104 14.0735-5.7896.0232-.0225.0462-.0453.0691-.0681.023-.023.0458-.0461.0684-.0693 3.5788-3.6137 5.789-8.5853 5.789-14.073 0-5.4788-2.203-10.443-5.7715-14.0552-.028-.02903-.0564-.05785-.085-.08647-.0283-.02828-.0568-.05629-.0854-.08403zm-17.4017 1.5465c-.4688-.1602-.9307-.2971-1.3835-.4103-3.551-.8877-5.98798-.2365-7.29325 1.0688s-1.9565 3.7423-1.06876 7.2932c.11322.4529.25005.9148.41032 1.3838 1.15492-1.7679 2.55915-3.5054 4.19489-5.1411 1.6354-1.6355 3.3727-3.0396 5.1403-4.1944zm16.0244 9.3353c-1.155-1.7679-2.5593-3.5055-4.1951-5.1413-1.6352-1.6352-3.3722-3.0391-5.1396-4.1939.4686-.1601.9303-.2969 1.3829-.41 3.5509-.8877 5.9879-.2365 7.2932 1.0687 1.3052 1.3053 1.9565 3.7423 1.0687 7.2933-.1132.4527-.2499.9145-.4101 1.3832z" fill="white" fillRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <div className="h-2 w-20 bg-zinc-700 rounded-full mb-1.5" />
                      <div className="h-1.5 w-12 bg-zinc-800 rounded-full" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="h-8 rounded-lg bg-zinc-800/50 border border-zinc-700/50 flex items-center px-3 gap-2">
                      <BrandIcons.Slack width={14} height={14} className="text-zinc-400" />
                      <div className="h-1.5 w-full bg-zinc-700/50 rounded-full" />
                    </div>
                    <div className="h-8 rounded-lg bg-zinc-800/50 border border-zinc-700/50 flex items-center px-3 gap-2">
                      <BrandIcons.Notion width={14} height={14} className="text-zinc-400" />
                      <div className="h-1.5 w-3/4 bg-zinc-700/50 rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Glow effect */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-purple-500/20 blur-3xl -z-10 rounded-full" />
              </div>
            </div>
          </div>
        </motion.div>
        )}

      </div>
    </section>
  );
}
